import { NgModule, ErrorHandler, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { environment } from 'environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { ClientsModule } from './clients/clients.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { GroupsModule } from './groups/groups.module';
import { ItinerariesModule } from './itineraries/itineraries.module';
import { StudentsModule } from './students/students.module';
import { LicensesModule } from './licenses/licenses.module';
import { MarsModule } from './mars/mars.module';
import { AuthModule } from './auth/auth.module';
import { EmailsModule } from './emails/emails.module';
import { RankingModule } from './ranking/ranking.module';
import { ErrorHandlerService, CsvService, BreadcrumbService, NotFoundComponent } from 'core/index';
import { AppRouteReuseStrategyService } from './services/routes/app-route-reuse-strategy.service';
import { AppTitleStrategyService } from './services/titles/app-title-strategy.service';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { PaymentsModule } from './payments';
import { ResourcesService } from './resources/services/resources.service';
import { httpInterceptorProviders } from './http-interceptors';
import { ReportsModule } from './reports/reports.module';
import { NotAvaliableComponent } from './not-avaliable/not-avaliable.component';
import { CoursesModule } from './courses/courses.module';
import { AccountModule } from './account/account.module';
import { Router, RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { RoleplayModule } from './roleplay/roleplay.module';
import { AgentsModule } from './agents/agents.module';
import { CustomizationsModule } from './customizations';
import { DemosModule } from './demos';
import { DocumentsModule } from './documents';
import { QuestionsModule } from './questions';
import { DistributorsModule } from './distributors';
import { GamesModule } from './games';
import { OrdersModule } from './orders/orders.module';
import { MonitorModule } from './monitor/monitor.module';
import { ResourcesModule } from './resources/resources.module';
import { SettingsModule } from './settings/settings.module';

@NgModule({
  declarations: [AppComponent, HomeComponent, NotAvaliableComponent, NotFoundComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CoreModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    // InMemoryWebApiModule.forRoot(InMemoryDataService, { passThruUnknownUrl: true, apiBase: '/api' }),
    ClientsModule,
    AccountModule,
    CoursesModule,
    DashboardModule,
    StudentsModule,
    MarsModule,
    LicensesModule,
    GroupsModule,
    ItinerariesModule,
    AuthModule,
    EmailsModule,
    RankingModule,
    PaymentsModule,
    DocumentsModule,
    DemosModule,
    QuestionsModule,
    DistributorsModule,
    GamesModule,
    ReportsModule,
    OrdersModule,
    ResourcesModule,
    SettingsModule,
    MonitorModule,
    RoleplayModule,
    AgentsModule,
    CustomizationsModule,
    AppRoutingModule // The app routing module must be the last imported one
  ],
  providers: [
    CsvService,
    Title,
    BreadcrumbService,
    ResourcesService,
    { provide: LOCALE_ID, useValue: localStorage.getItem('backofficelanguage') || 'en' },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: TitleStrategy, useClass: AppTitleStrategyService },
    httpInterceptorProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true
    },
    {
      provide: TraceService,
      deps: [Router]
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
        logErrors: true
      })
    },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategyService },

    provideCharts(withDefaultRegisterables()),
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, environment.TRANSLATIONS_URL, '.json');
}
